import React, { useState } from 'react';
import { BrowserRouter } from 'react-router';
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
  MutationCache,
} from '@tanstack/react-query';

import AuthProvider from './auth/AuthProvider';
import { TenantProvider } from './context/TenantContext';
import { EnvProvider } from './context/EnvContext';
import AppBootstrap from './auth/AppBootstrap';

/**
 * Factory rather than module-scoped singleton so each mount (and each test)
 * gets its own client. Prevents cache leakage across tests and forced remounts.
 */
const createQueryClient = (): QueryClient =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: 1,
        // Admin portal — users tab around frequently; surprise refetches on
        // window focus are disruptive. Opt individual queries in as needed.
        refetchOnWindowFocus: false,
      },
      mutations: {
        // POST/PUT are rarely idempotent; don't retry by default. Each
        // mutation can opt in explicitly.
        retry: 0,
      },
    },
    queryCache: new QueryCache({
      onError: (err) => {
        // eslint-disable-next-line no-console
        console.warn('[QueryCache] error:', err);
      },
    }),
    mutationCache: new MutationCache({
      onError: (err) => {
        // eslint-disable-next-line no-console
        console.warn('[MutationCache] error:', err);
      },
    }),
  });

/**
 * Data / auth / tenant / routing providers. Intentionally separate from
 * ThemeProviders so theme is available to the top-level error boundary even
 * when one of these throws.
 *
 * Order notes:
 *   - QueryClientProvider outermost: any provider below it may plausibly use
 *     react-query (e.g. Auth0 token refresh caching) without breaking.
 *   - BrowserRouter before AuthProvider: AuthProvider uses `useNavigate`
 *     inside `onRedirectCallback` to restore deep links after login.
 *   - AuthProvider before TenantProvider: TenantProvider calls useAuth0.
 */
const Providers: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [queryClient] = useState(createQueryClient);
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <AppBootstrap />
          {/* EnvProvider between AuthProvider and TenantProvider so usePromoteGating
              + useIsEditable resolve without requiring a tenant context first. */}
          <EnvProvider>
            <TenantProvider>{children}</TenantProvider>
          </EnvProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default Providers;
